import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import { Row, Col, Tab, Tabs, Spinner, Navbar, Nav } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

import './GuidelinesPage.css';

import { useAppState } from '../AppContext';

import FilterForm from '../components/FilterForm';
import FileUpload from '../components/FileUpload';
import SearchForm from '../components/SearchForm';
import GuidelineTable from '../components/GuidelineTable';
import Pdf from '../components/Pdf';
import { getAvailableHeight } from '../components/utils';
import ClusterScatterplot from '../components/scatterplots/ClusterScatterplot';
import ClusterList from '../components/ClusterList';

import { tourSteps, CustomBeacon } from '../components/Tour';
import Joyride from 'react-joyride';
import { FaInfo, FaRegComment } from 'react-icons/fa';
import { GrHelp } from 'react-icons/gr';
import DocumentTermMatrix from '../components/matrix/DocumentTermMatrix';

function CombinedPage() {
  const [pageNumber, setPageNumber] = useState(1);

  const [filterString, setFilterString] = useState('');

  const [overlays, setOverlays] = useState([]);

  let [
    {
      documents,
      selectedDocument,
      clusterData,
      documentResults,
      searchString,
      searchInProgress
    },
    dispatch
  ] = useAppState();

  const highlightSnippet = (snippet, document) => {
    dispatch({
      type: 'SET_SELECTED_SNIPPET',
      selectedSnippet: snippet ? snippet.id : null
    });
    setOverlays(snippet ? [snippet.misc] : []);
    dispatch({ type: 'SET_SELECTED_DOCUMENT', selectedDocument: document });
    setPageNumber(snippet ? +snippet.misc.page : 1);
  };

  const searchFulltext = (newSearchString) => {
    dispatch({ type: 'SET_SELECTED_CLUSTER', selectedCluster: false });

    // if search string is empty, then clear search results
    if (!newSearchString) {
      dispatch({ type: 'SET_SEARCH_STRING', searchString: newSearchString });
      dispatch({ type: 'SET_DOCUMENT_RESULTS', documentResults: false });
      return;
    }

    const query = { method: 'solr', textQuery: newSearchString };

    dispatch({ type: 'SET_SELECTED_DOCUMENT', selectedDocument: false });
    dispatch({ type: 'SET_SELECTED_SNIPPET', selectedSnippet: false });
    dispatch({ type: 'SET_SEARCH_STRING', searchString: newSearchString });

    dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: true });

    fetch('/nlp/documents/search', {
      method: 'POST', // or 'PUT'
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(query)
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Success:', data);
        const docs = data.documents;
        docs.sort((a, b) => b.score - a.score);
        dispatch({ type: 'SET_DOCUMENT_RESULTS', documentResults: docs });
        dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: false });
      })
      .catch((error) => {
        console.error('Error on search:', error);
        dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: false });
      });
  };

  // fetch documents
  useEffect(() => {
    fetch('/api/documents')
      .then((res) => res.json())
      .then((data) => {
        if (data.documents.length > 0) {
          dispatch({ type: 'SET_DOCUMENTS', documents: data.documents });
        }
      });
  }, [dispatch]);

  // fetch cluster data
  useEffect(() => {
    fetch('/nlp/documents/clusters', {
      method: 'GET', // or 'PUT'
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.clustergroup && data.clustergroup.clusters) {
          dispatch({
            type: 'SET_CLUSTER_DATA',
            clusterData: data.clustergroup.clusters
          });
        }
      });
  }, [dispatch]);

  const filterDocument = (doc) => {
    const fields = [
      'title',
      'filename',
      'version',
      'edit_date',
      'org_name',
      'org_level'
    ];
    for (let i = 0; i < fields.length; i++) {
      const value = doc[fields[i]];
      if (value && value.toLowerCase().includes(filterString.toLowerCase())) {
        return true;
      }
    }
  };

  if (!documents) {
    return (
      <Row className="limitedWidth">
        <Col>
          <h1>
            <Spinner
              as="span"
              animation="grow"
              role="status"
              aria-hidden="true"
            />
            Loading...
          </h1>
        </Col>
      </Row>
    );
  }

  let documentsInTable;
  if (documents) {
    if (documentResults && searchString) {
      // if searchString is empty, the search results will be empty too, so don't filter using them
      // could directly filter documentResults isntead?
      const resultDocumentIds = documentResults
        .filter((doc) => filterDocument(doc))
        .map((doc) => doc.id);
      documentsInTable = documents.filter(
        (doc) => filterDocument(doc) && resultDocumentIds.includes(doc.id)
      );
    } else {
      documentsInTable = documents.filter((doc) => filterDocument(doc));
    }
  }

  const HeaderBar = () => {
    return (
      <div
        className="limitedWidth"
        style={{
          borderRadius: '10px',
          padding: '10px',
          position: 'relative'
        }}
      >
        <div className="background-image" />

        <Row style={{ paddingLeft: '10px', lineHeight: '2.5rem' }}>
          <Col sm={7}>
            <h1>{process.env.REACT_APP_TITLE}</h1>
          </Col>

          <Col sm={5}>
            <Navbar style={{}} expand="lg" className={'navbar-right'}>
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
              <Navbar.Collapse id="basic-navbar-nav" className={'navbar-right'}>
                <Nav variant="pills">
                  <Nav.Item>
                    <Nav.Link href="/about" id="about-link">
                      <FaInfo /> About
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link href="/how-it-works" id="how-it-work-link">
                      <GrHelp /> How it works
                    </Nav.Link>
                  </Nav.Item>

                  {process.env.REACT_APP_FEEDBACK_LINK && (
                    <Nav.Item>
                      <Nav.Link
                        href={process.env.REACT_APP_FEEDBACK_LINK}
                        id="feedback-link"
                      >
                        <FaRegComment /> Give feedback
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </Navbar.Collapse>
            </Navbar>
          </Col>
        </Row>

        <Row>
          <Col id="search-col">
            <div>
              <SearchForm
                searchFulltext={searchFulltext}
                searchString={searchString}
              />
              <FilterForm
                setFilterString={setFilterString}
                onInteraction={(ev) =>
                  dispatch({
                    type: 'SET_SELECTED_CLUSTER',
                    selectedCluster: false
                  })
                }
                filterString={filterString}
              />{' '}
            </div>
          </Col>

          <Col id="upload-col">
            <FileUpload redirect={<Redirect to={'/upload'} />} />
          </Col>
        </Row>
      </div>
    );
  };

  if (document.getElementsByTagName('html')[0].clientWidth < 1000) {
    const pageWidth = document.getElementsByTagName('html')[0].clientWidth - 50;

    return (
      <>
        <Joyride
          steps={tourSteps}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          beaconComponent={CustomBeacon}
        />

        <HeaderBar />

        <p>
          You are viewing the mobile version of this site, which has reduced
          functionality. It is recommended to use a laptop or desktop device
          with a larger resolution.
        </p>

        <Row>
          <Col
            xs={12}
            style={{
              height: window.innerWidth * Math.sqrt(2),
              overflowY: 'auto',
              marginTop: '15px'
            }}
          >
            {selectedDocument ? (
              <Pdf
                url={`/api/uploads/${selectedDocument.id}/${selectedDocument.filename}`}
                pageNumber={pageNumber}
                setPageNumber={(page) => setPageNumber(page)}
                searchString={searchString}
                overlays={overlays}
                className="pdf-div"
                pageWidth={pageWidth}
              />
            ) : (
              <div style={{ display: 'flex', height: '100%' }}>
                <h1 style={{ margin: 'auto' }}>
                  Select a document to preview.
                </h1>
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col
            xs={12}
            style={{
              height: Math.max(800, getAvailableHeight() - 250),
              overflowY: 'auto',
              overflowWrap: 'break-word',
              marginTop: '15px'
            }}
          >
            {searchInProgress ? (
              <div style={{ display: 'flex', height: '100%' }}>
                <h1 style={{ margin: 'auto' }}>
                  Searching...
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    aria-hidden="true"
                  />
                </h1>
              </div>
            ) : (
              <Tabs
                defaultActiveKey="clusters"
                id="results-tabs"
                onSelect={(tabName) =>
                  tabName === 'table' &&
                  highlightSnippet(null, selectedDocument)
                }
              >
                <Tab eventKey="clusters" title="Clusters">
                  {documents && (
                    <ClusterList
                      documentsInTable={documentsInTable}
                      highlightSnippet={highlightSnippet}
                    />
                  )}
                </Tab>

                <Tab eventKey="table" title="Documents">
                  {documents && (
                    <GuidelineTable documentsInTable={documentsInTable} />
                  )}
                </Tab>
              </Tabs>
            )}
          </Col>
        </Row>
      </>
    );
  }

  const maxPageWidth =
    document.getElementsByTagName('html')[0].clientWidth * (4 / 12);
  const maxPageHeight =
    document.getElementsByTagName('html')[0].clientHeight * 0.6;
  const pageWidth = Math.min(maxPageWidth, maxPageHeight / Math.sqrt(2));

  return (
    <>
      <Joyride
        steps={tourSteps}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        beaconComponent={CustomBeacon}
      />

      <HeaderBar />

      <Row>
        <Col
          xs={4}
          style={{
            height: Math.max(800, getAvailableHeight() - 250),
            overflowY: 'auto',
            marginTop: '15px'
          }}
        >
          {selectedDocument ? (
            <Pdf
              url={`/api/uploads/${selectedDocument.id}/${selectedDocument.filename}`}
              pageNumber={pageNumber}
              setPageNumber={(page) => setPageNumber(page)}
              searchString={searchString}
              overlays={overlays}
              className="pdf-div"
              pageWidth={pageWidth}
            />
          ) : (
            <div style={{ display: 'flex', height: '100%' }}>
              <h1 style={{ margin: 'auto' }}>Select a document to preview.</h1>
            </div>
          )}
        </Col>

        <Col
          xs={4}
          style={{
            height: Math.max(800, getAvailableHeight() - 250),
            overflowY: 'auto',
            marginTop: '15px'
          }}
        >
          {clusterData && (
            <>
              <ClusterScatterplot
                filterDocument={filterDocument}
                highlightSnippet={highlightSnippet}
                includeText={true}
              />
              <small className="text-muted form-text">
                Each dot in this plot represents a document. They are arranged
                so that similar documents are close together. Documents are
                assigned to a <i>cluster</i>, and coloured accordingly. You can
                mouse-over a document to see its title, or click to open it.
              </small>

              <br />
            </>
          )}
        </Col>

        <Col
          xs={4}
          style={{
            height: Math.max(800, getAvailableHeight() - 250),
            overflowY: 'auto',
            marginTop: '15px'
          }}
        >
          {searchInProgress ? (
            <div style={{ display: 'flex', height: '100%' }}>
              <h1 style={{ margin: 'auto' }}>
                Searching...
                <Spinner
                  as="span"
                  animation="border"
                  role="status"
                  aria-hidden="true"
                />
              </h1>
            </div>
          ) : (
            <Tabs
              defaultActiveKey="clusters"
              id="results-tabs"
              onSelect={(tabName) =>
                tabName === 'table' && highlightSnippet(null, selectedDocument)
              }
            >
              <Tab eventKey="clusters" title="Clusters">
                {documents && (
                  <ClusterList
                    documentsInTable={documentsInTable}
                    highlightSnippet={highlightSnippet}
                  />
                )}
              </Tab>

              <Tab eventKey="table" title="Documents">
                {documents && (
                  <GuidelineTable documentsInTable={documentsInTable} />
                )}
              </Tab>

              <Tab eventKey="matrix" title="Matrix">
                {documents && (
                  <DocumentTermMatrix
                    documentsInTable={documentsInTable}
                    filterDocument={filterDocument}
                    highlightSnippet={highlightSnippet}
                  />
                )}
              </Tab>
            </Tabs>
          )}
        </Col>
      </Row>
    </>
  );
}

export default CombinedPage;
